import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BriefcaseBusiness, LayoutGrid, List, Plus, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useHiringApi, errMsg } from '../../components/admin/hiring/useHiringApi';
import { ALL_STAGES, ARRANGEMENTS, SIDE_STAGES, SOURCES, STAGES, fmtDate, fullName, stageMeta } from '../../components/admin/hiring/hiringConstants';
import AddApplicantDialog from '../../components/admin/hiring/AddApplicantDialog';

const ANY = '__any';

const ApplicantCard = ({ a }) => (
  <Link to={`/admin/hiring/${a.id}`} className="block bg-white border border-zinc-200 rounded-lg p-3 hover:border-orange-300 hover:shadow-sm transition" data-testid={`applicant-card-${a.id}`}>
    <div className="flex items-start justify-between gap-2">
      <p className="font-semibold text-sm text-zinc-900 truncate">{fullName(a)}</p>
      {a.is_repeat && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Repeat</Badge>}
    </div>
    <p className="text-xs text-zinc-500 truncate">{a.arrangement || 'Arrangement TBD'} · {a.city || '—'}</p>
    <div className="flex items-center justify-between mt-2 text-[11px] text-zinc-400">
      <span>{fmtDate(a.applied_at)}</span>
      <span className="truncate ml-2">{a.owner_name ? `Owner: ${a.owner_name.split(' ')[0]}` : 'Unassigned'}</span>
    </div>
    {a.next_action?.text && <p className="mt-2 text-[11px] text-orange-700 truncate">→ {a.next_action.text}</p>}
  </Link>
);

const Board = ({ items }) => (
  <div className="flex gap-3 overflow-x-auto pb-4" data-testid="hiring-board">
    {STAGES.map((s) => {
      const col = items.filter((a) => a.stage === s.key);
      return (
        <div key={s.key} className="min-w-[230px] w-[230px] flex-shrink-0 bg-zinc-100/80 rounded-xl p-2" data-testid={`board-col-${s.key}`}>
          <div className="flex items-center justify-between px-1 mb-2">
            <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${s.tone}`}>{s.label}</span>
            <span className="text-xs text-zinc-500" data-testid={`board-count-${s.key}`}>{col.length}</span>
          </div>
          <div className="space-y-2">{col.map((a) => <ApplicantCard key={a.id} a={a} />)}</div>
        </div>
      );
    })}
  </div>
);

const Table = ({ items }) => (
  <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="hiring-list">
    <table className="w-full text-sm">
      <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
        <tr><th className="px-4 py-3">Applicant</th><th className="px-4 py-3">Stage</th><th className="px-4 py-3 hidden md:table-cell">Arrangement</th><th className="px-4 py-3 hidden lg:table-cell">Source</th><th className="px-4 py-3 hidden md:table-cell">Owner</th><th className="px-4 py-3">Applied</th></tr>
      </thead>
      <tbody>
        {items.map((a) => {
          const m = stageMeta(a.stage);
          return (
            <tr key={a.id} className="border-t border-zinc-100 hover:bg-orange-50/40">
              <td className="px-4 py-3">
                <Link to={`/admin/hiring/${a.id}`} className="font-semibold text-zinc-900 hover:text-orange-700" data-testid={`applicant-row-${a.id}`}>{fullName(a)}</Link>
                <p className="text-xs text-zinc-500">{a.email || a.phone}{a.is_repeat ? ' · repeat applicant' : ''}</p>
              </td>
              <td className="px-4 py-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded ${m.tone}`}>{m.label}</span></td>
              <td className="px-4 py-3 hidden md:table-cell text-zinc-600">{a.arrangement || '—'}</td>
              <td className="px-4 py-3 hidden lg:table-cell text-zinc-600">{a.recruiting_source || '—'}</td>
              <td className="px-4 py-3 hidden md:table-cell text-zinc-600">{a.owner_name || 'Unassigned'}</td>
              <td className="px-4 py-3 text-zinc-600">{fmtDate(a.applied_at)}</td>
            </tr>
          );
        })}
        {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-400" data-testid="hiring-empty">No applicants match these filters.</td></tr>}
      </tbody>
    </table>
  </div>
);

export default function AdminHiringPage() {
  const api = useHiringApi();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('board');
  const [q, setQ] = useState('');
  const [stage, setStage] = useState(ANY);
  const [source, setSource] = useState(ANY);
  const [arrangement, setArrangement] = useState(ANY);
  const [owner, setOwner] = useState(ANY);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q, stage: stage === ANY ? '' : stage, source: source === ANY ? '' : source, arrangement: arrangement === ANY ? '' : arrangement, owner_id: owner === ANY ? '' : owner };
      const [list, st] = await Promise.all([api.list(params), api.stats()]);
      setItems(list); setStats(st);
    } catch (e) { toast.error(errMsg(e, 'Failed to load applicants')); }
    finally { setLoading(false); }
  }, [api, q, stage, source, arrangement, owner]);

  useEffect(() => { api.config().then(setCfg).catch(() => {}); }, [api]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const sideCounts = useMemo(() => SIDE_STAGES.map((s) => ({ ...s, n: stats?.by_stage?.[s.key] || 0 })), [stats]);

  return (
    <>
      <div className="min-h-screen" data-testid="admin-hiring-page">
        <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Link to="/admin/dashboard" className="text-zinc-500 hover:text-zinc-900" data-testid="back-to-dashboard"><ArrowLeft className="w-5 h-5" /></Link>
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center"><BriefcaseBusiness className="w-5 h-5 text-orange-600" /></div>
                <div className="min-w-0">
                  <h1 className="text-lg font-bold text-zinc-900">Hiring</h1>
                  <p className="text-xs text-zinc-500 truncate">Become A Guru applications · {stats?.total ?? '—'} total{cfg && !cfg.email_configured ? ' · Email not configured' : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={load} data-testid="hiring-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
                <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={() => setAddOpen(true)} data-testid="hiring-add-applicant"><Plus className="w-4 h-4 mr-1" /> Add applicant</Button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, city" className="pl-9" data-testid="hiring-search" />
            </div>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger className="w-[150px]" data-testid="hiring-filter-stage"><SelectValue placeholder="Stage" /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>All stages</SelectItem>{ALL_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="w-[150px]" data-testid="hiring-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>All sources</SelectItem>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={arrangement} onValueChange={setArrangement}>
              <SelectTrigger className="w-[190px]" data-testid="hiring-filter-arrangement"><SelectValue placeholder="Arrangement" /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>All arrangements</SelectItem>{ARRANGEMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={owner} onValueChange={setOwner}>
              <SelectTrigger className="w-[160px]" data-testid="hiring-filter-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>Any owner</SelectItem>{(cfg?.staff || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-1 bg-white border border-zinc-200 rounded-lg p-0.5">
              <button type="button" onClick={() => setView('board')} className={`p-1.5 rounded ${view === 'board' ? 'bg-zinc-900 text-white' : 'text-zinc-500'}`} aria-label="Board view" data-testid="hiring-view-board"><LayoutGrid className="w-4 h-4" /></button>
              <button type="button" onClick={() => setView('list')} className={`p-1.5 rounded ${view === 'list' ? 'bg-zinc-900 text-white' : 'text-zinc-500'}`} aria-label="List view" data-testid="hiring-view-list"><List className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 text-xs">
            {sideCounts.map((s) => (
              <button key={s.key} type="button" onClick={() => setStage(stage === s.key ? ANY : s.key)} className={`px-2.5 py-1 rounded-full border ${stage === s.key ? 'border-zinc-900 bg-zinc-900 text-white' : `border-transparent ${s.tone}`}`} data-testid={`side-stage-${s.key}`}>
                {s.label} · {s.n}
              </button>
            ))}
          </div>

          {loading && items.length === 0 ? <p className="text-sm text-zinc-400 py-10 text-center">Loading…</p>
            : view === 'board' && stage === ANY ? <Board items={items} /> : <Table items={items} />}
        </main>

        <AddApplicantDialog open={addOpen} onOpenChange={setAddOpen} onCreated={(a) => { setAddOpen(false); load(); toast.success(`${fullName(a)} added to Hiring`); }} />
      </div>
    </>
  );
}
